/**
 * Performance PDF Export Utility
 * Generates professional PDF reports from performance data
 *
 * Features:
 * - Role-based comprehensive exports (Employee, Manager, Admin)
 * - Professional formatting with tables, charts representation, and branding
 * - Proper section separation and page management
 */

import { jsPDF } from 'jspdf';
import type {
  PerformanceData,
  TeamPerformanceSummary,
  OrganizationPerformanceSummary,
  PeriodType,
  ScoreDistribution,
} from '../types';

// ============================================
// TYPES
// ============================================

export interface ComprehensiveExportData {
  individual?: PerformanceData | null;
  team?: TeamPerformanceSummary | null;
  organization?: OrganizationPerformanceSummary | null;
}

export interface PDFExportOptions {
  title?: string;
  subtitle?: string;
  tenantName?: string;
  includeTableOfContents?: boolean;
}

// ============================================
// CONSTANTS
// ============================================

const COLORS = {
  primary: { r: 37, g: 99, b: 235 },      // blue-600
  secondary: { r: 100, g: 116, b: 139 },  // slate-500
  dark: { r: 15, g: 23, b: 42 },          // slate-900
  muted: { r: 148, g: 163, b: 184 },      // slate-400
  light: { r: 241, g: 245, b: 249 },      // slate-100
  white: { r: 255, g: 255, b: 255 },
  border: { r: 226, g: 232, b: 240 },     // slate-200
  excellent: { r: 16, g: 185, b: 129 },   // emerald-500
  good: { r: 59, g: 130, b: 246 },        // blue-500
  warning: { r: 245, g: 158, b: 11 },     // amber-500
  poor: { r: 239, g: 68, b: 68 },         // red-500
};

const PERIOD_LABELS: Record<PeriodType, string> = {
  monthly: 'Monthly',
  quarterly: 'Quarterly',
  half_yearly: 'Half-Yearly',
  yearly: 'Yearly',
};

const FONTS = {
  title: 24,
  heading1: 18,
  heading2: 14,
  heading3: 12,
  body: 10,
  small: 9,
  tiny: 8,
};

// ============================================
// HELPER FUNCTIONS
// ============================================

function getScoreColorRGB(score: number): { r: number; g: number; b: number } {
  if (score >= 90) return COLORS.excellent;
  if (score >= 70) return COLORS.good;
  if (score >= 50) return COLORS.warning;
  return COLORS.poor;
}

function getScoreLabel(score: number): string {
  if (score >= 90) return 'Excellent';
  if (score >= 70) return 'Good';
  if (score >= 50) return 'Needs Improvement';
  return 'Poor';
}

function getTrendText(trend: 'up' | 'down' | 'stable' | undefined): string {
  if (trend === 'up') return 'Up';
  if (trend === 'down') return 'Down';
  return 'Stable';
}

function formatDate(dateString?: string): string {
  if (!dateString) return '';
  try {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return dateString;
  }
}

function setColor(doc: jsPDF, color: { r: number; g: number; b: number }): void {
  doc.setTextColor(color.r, color.g, color.b);
}

function setDrawColor(doc: jsPDF, color: { r: number; g: number; b: number }): void {
  doc.setDrawColor(color.r, color.g, color.b);
}

function setFillColor(doc: jsPDF, color: { r: number; g: number; b: number }): void {
  doc.setFillColor(color.r, color.g, color.b);
}

// ============================================
// PDF COMPONENTS
// ============================================

/**
 * The one place a performance PDF decides how to describe whose figures it holds.
 *
 * PROVENANCE, NOT A CONTROL — see the note on `drawHeader`. It is a pure function so it
 * can be tested without rendering a PDF, and so the three export entry points cannot
 * drift into three different wordings. `reportType` is derived from which sections the
 * API actually returned, never from the caller's role: the server decides what it sends,
 * and this only reports what arrived.
 */
export function describePerformanceScope(
  reportType: 'Individual' | 'Team' | 'Organization',
  subjectName?: string
): string {
  if (reportType === 'Organization') return 'Organization-wide';
  if (reportType === 'Team') {
    return subjectName ? `${subjectName} and their direct reports` : 'A team and its direct reports';
  }
  return subjectName ? `${subjectName} only` : 'A single individual';
}

/**
 * Draw the professional header with branding
 *
 * `scopeLabel` is PROVENANCE, NOT A CONTROL. It states whose figures the reader is
 * holding, because a printed "Average progress 62%" is unreadable without that. It
 * enforces nothing: this function runs in the browser on data the API has already
 * decided to send, so nothing here can widen or narrow what was exported. The real
 * scoping happens server-side (`backend/src/utils/reportScope.ts` for reports,
 * `canReadOKR` in `export.service.ts` for exports). Do not describe this line as a
 * privacy measure, and never let a caller pass a scope label that contradicts what
 * the server actually returned.
 */
function drawHeader(
  doc: jsPDF,
  title: string,
  subtitle?: string,
  periodLabel?: string,
  tenantName?: string,
  scopeLabel?: string
): number {
  const pageWidth = doc.internal.pageSize.getWidth();
  let yPos = 0;

  // Header background bar
  setFillColor(doc, COLORS.primary);
  doc.rect(0, 0, pageWidth, 35, 'F');

  // App name and Tenant name
  doc.setFontSize(FONTS.heading3);
  setColor(doc, COLORS.white);
  doc.setFont('helvetica', 'bold');
  const headerText = tenantName ? `OKR Hub - ${tenantName}` : 'OKR Hub';
  doc.text(headerText, 20, 15);

  // "Performance Report" badge
  doc.setFontSize(FONTS.small);
  doc.setFont('helvetica', 'normal');
  doc.text('Performance Report', 20, 25);

  // Generated date on the right
  doc.setFontSize(FONTS.tiny);
  const generatedText = `Generated: ${new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })}`;
  doc.text(generatedText, pageWidth - 20, 15, { align: 'right' });

  yPos = 45;

  // Main title
  doc.setFontSize(FONTS.title);
  setColor(doc, COLORS.dark);
  doc.setFont('helvetica', 'bold');
  doc.text(title, 20, yPos);
  yPos += 10;

  // Subtitle
  if (subtitle) {
    doc.setFontSize(FONTS.heading3);
    setColor(doc, COLORS.secondary);
    doc.setFont('helvetica', 'normal');
    doc.text(subtitle, 20, yPos);
    yPos += 8;
  }

  // Period label
  if (periodLabel) {
    doc.setFontSize(FONTS.body);
    setColor(doc, COLORS.muted);
    doc.text(`Period: ${periodLabel}`, 20, yPos);
    yPos += 5;
  }

  // Scope line — provenance only; see the note on this function.
  if (scopeLabel) {
    doc.setFontSize(FONTS.body);
    setColor(doc, COLORS.muted);
    doc.text(`Scope: ${scopeLabel}`, 20, yPos);
    yPos += 5;
  }

  // Divider line
  yPos += 5;
  setDrawColor(doc, COLORS.border);
  doc.setLineWidth(0.5);
  doc.line(20, yPos, pageWidth - 20, yPos);

  return yPos + 10;
}

/**
 * Draw footer with page numbers
 */
function drawFooter(doc: jsPDF, pageNumber: number, totalPages: number): void {
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();

  // Footer line
  setDrawColor(doc, COLORS.border);
  doc.setLineWidth(0.3);
  doc.line(20, pageHeight - 20, pageWidth - 20, pageHeight - 20);

  // Page number
  doc.setFontSize(FONTS.tiny);
  setColor(doc, COLORS.muted);
  doc.text(`Page ${pageNumber} of ${totalPages}`, pageWidth / 2, pageHeight - 12, { align: 'center' });

  // Branding
  doc.text('OKR Hub Performance Report', 20, pageHeight - 12);

  // Confidential notice
  doc.text('Confidential', pageWidth - 20, pageHeight - 12, { align: 'right' });
}

/**
 * Draw a section header
 */
function drawSectionHeader(doc: jsPDF, title: string, yPos: number, _icon?: string): number {
  const pageWidth = doc.internal.pageSize.getWidth();

  // Section background
  setFillColor(doc, COLORS.light);
  doc.rect(20, yPos - 5, pageWidth - 40, 12, 'F');

  // Section title
  doc.setFontSize(FONTS.heading2);
  setColor(doc, COLORS.dark);
  doc.setFont('helvetica', 'bold');
  doc.text(title, 25, yPos + 3);

  return yPos + 15;
}

/**
 * Draw a subsection header
 */
function drawSubsectionHeader(doc: jsPDF, title: string, yPos: number): number {
  doc.setFontSize(FONTS.heading3);
  setColor(doc, COLORS.dark);
  doc.setFont('helvetica', 'bold');
  doc.text(title, 20, yPos);

  return yPos + 8;
}

/**
 * Draw the score gauge visualization
 */
function drawScoreGauge(
  doc: jsPDF,
  score: number,
  x: number,
  y: number,
  size: number = 40
): void {
  const color = getScoreColorRGB(score);

  // Outer circle (background)
  setFillColor(doc, COLORS.light);
  doc.circle(x, y, size / 2, 'F');

  // Score arc (progress)
  // Note: jsPDF doesn't support arc drawing, so we use circles instead
  // const progress = score / 100;

  // Draw progress arc using filled wedge
  setFillColor(doc, color);
  doc.setDrawColor(color.r, color.g, color.b);
  doc.setLineWidth(4);

  // Simple representation: filled circle with score
  doc.circle(x, y, size / 2 - 5, 'S');

  // Inner circle (white background for text)
  setFillColor(doc, COLORS.white);
  doc.circle(x, y, size / 2 - 8, 'F');

  // Score text
  doc.setFontSize(FONTS.heading1);
  setColor(doc, color);
  doc.setFont('helvetica', 'bold');
  doc.text(Math.round(score).toString(), x, y + 2, { align: 'center' });

  // Label below
  doc.setFontSize(FONTS.tiny);
  setColor(doc, COLORS.muted);
  doc.setFont('helvetica', 'normal');
  doc.text(getScoreLabel(score), x, y + size / 2 + 8, { align: 'center' });
}

/**
 * Draw a horizontal progress bar
 */
function drawProgressBar(
  doc: jsPDF,
  value: number,
  x: number,
  y: number,
  width: number,
  height: number = 6
): void {
  const color = getScoreColorRGB(value);
  const progress = Math.min(100, Math.max(0, value)) / 100;

  // Background
  setFillColor(doc, COLORS.light);
  doc.roundedRect(x, y, width, height, 2, 2, 'F');

  // Progress
  if (progress > 0) {
    setFillColor(doc, color);
    doc.roundedRect(x, y, width * progress, height, 2, 2, 'F');
  }
}

/**
 * Draw a data table
 */
function drawTable(
  doc: jsPDF,
  headers: string[],
  rows: (string | number)[][],
  x: number,
  yStart: number,
  colWidths: number[],
  options: {
    headerBg?: { r: number; g: number; b: number };
    alternateRows?: boolean;
    scoreColumn?: number;
  } = {}
): number {
  const { headerBg = COLORS.primary, alternateRows = true, scoreColumn } = options;
  const rowHeight = 8;
  const cellPadding = 3;
  let yPos = yStart;

  // Header row
  setFillColor(doc, headerBg);
  const totalWidth = colWidths.reduce((sum, w) => sum + w, 0);
  doc.rect(x, yPos, totalWidth, rowHeight + 2, 'F');

  doc.setFontSize(FONTS.small);
  setColor(doc, COLORS.white);
  doc.setFont('helvetica', 'bold');

  let xPos = x + cellPadding;
  headers.forEach((header, i) => {
    doc.text(header, xPos, yPos + rowHeight - 1);
    xPos += colWidths[i];
  });

  yPos += rowHeight + 2;

  // Data rows
  doc.setFont('helvetica', 'normal');
  rows.forEach((row, rowIndex) => {
    // Alternate row background
    if (alternateRows && rowIndex % 2 === 1) {
      setFillColor(doc, COLORS.light);
      doc.rect(x, yPos, totalWidth, rowHeight, 'F');
    }

    xPos = x + cellPadding;
    row.forEach((cell, colIndex) => {
      const cellValue = typeof cell === 'number' ? Math.round(cell).toString() : cell;

      // Color score columns
      if (colIndex === scoreColumn && typeof cell === 'number') {
        setColor(doc, getScoreColorRGB(cell));
      } else {
        setColor(doc, COLORS.dark);
      }

      // Truncate long text
      const maxWidth = colWidths[colIndex] - cellPadding * 2;
      let displayText = cellValue;
      while (doc.getTextWidth(displayText) > maxWidth && displayText.length > 3) {
        displayText = displayText.slice(0, -4) + '...';
      }

      doc.text(displayText, xPos, yPos + rowHeight - 2);
      xPos += colWidths[colIndex];
    });

    yPos += rowHeight;
  });

  // Bottom border
  setDrawColor(doc, COLORS.border);
  doc.setLineWidth(0.3);
  doc.line(x, yPos, x + totalWidth, yPos);

  return yPos + 5;
}

/**
 * Draw score distribution chart (horizontal bars)
 */
function drawDistributionChart(
  doc: jsPDF,
  distribution: ScoreDistribution,
  x: number,
  yPos: number,
  width: number
): number {
  const total = distribution.excellent + distribution.good + distribution.needsImprovement + distribution.poor;
  if (total === 0) return yPos;

  // Use shorter labels to fit better
  const categories = [
    { label: 'Excellent (90+)', count: distribution.excellent, color: COLORS.excellent },
    { label: 'Good (70-89)', count: distribution.good, color: COLORS.good },
    { label: 'Needs Work (50-69)', count: distribution.needsImprovement, color: COLORS.warning },
    { label: 'Poor (0-49)', count: distribution.poor, color: COLORS.poor },
  ];

  const barHeight = 12;
  const labelWidth = 50;  // Reduced from 80
  const countWidth = 30;  // Space for count display
  const barWidth = Math.max(20, width - labelWidth - countWidth - 10);

  categories.forEach((cat) => {
    const percentage = (cat.count / total) * 100;

    // Label (smaller font)
    doc.setFontSize(FONTS.tiny);
    setColor(doc, COLORS.dark);
    doc.text(cat.label, x, yPos + 3);

    // Background bar
    setFillColor(doc, COLORS.light);
    doc.roundedRect(x + labelWidth, yPos - 2, barWidth, 8, 2, 2, 'F');

    // Filled portion
    if (percentage > 0) {
      setFillColor(doc, cat.color);
      const fillWidth = Math.max(4, (barWidth * percentage) / 100);
      doc.roundedRect(x + labelWidth, yPos - 2, fillWidth, 8, 2, 2, 'F');
    }

    // Count and percentage
    doc.setFontSize(FONTS.tiny);
    setColor(doc, COLORS.secondary);
    doc.text(`${cat.count} (${percentage.toFixed(0)}%)`, x + labelWidth + barWidth + 3, yPos + 3);

    yPos += barHeight;
  });

  return yPos + 5;
}

/**
 * Check if we need a new page and add one if necessary
 */
function checkPageBreak(doc: jsPDF, yPos: number, requiredSpace: number = 50): number {
  const pageHeight = doc.internal.pageSize.getHeight();
  if (yPos + requiredSpace > pageHeight - 30) {
    doc.addPage();
    return 20;
  }
  return yPos;
}

// ============================================
// SECTION RENDERERS
// ============================================

/**
 * Render Individual Performance Section
 */
function renderIndividualSection(
  doc: jsPDF,
  data: PerformanceData,
  yPos: number,
  isFirstSection: boolean = false
): number {
  const pageWidth = doc.internal.pageSize.getWidth();

  if (!isFirstSection) {
    doc.addPage();
    yPos = 20;
  }

  // Section header
  yPos = drawSectionHeader(doc, 'My Performance', yPos);
  yPos += 5;

  // User info
  doc.setFontSize(FONTS.body);
  setColor(doc, COLORS.secondary);
  doc.text(`Employee: ${data.userName}`, 20, yPos);
  doc.text(`Email: ${data.userEmail}`, pageWidth / 2, yPos);
  yPos += 12;

  // Score gauge and breakdown side by side
  // A4 page is 210mm wide, with 20mm margins = 170mm usable
  const gaugeX = 45;
  const gaugeSize = 40;
  drawScoreGauge(doc, data.score.overall, gaugeX, yPos + 20, gaugeSize);

  // Score breakdown on the right (starts after gauge)
  const breakdownX = 80;
  const breakdownYStart = yPos;
  doc.setFontSize(FONTS.heading3);
  setColor(doc, COLORS.dark);
  doc.setFont('helvetica', 'bold');
  doc.text('Score Breakdown', breakdownX, yPos);
  yPos += 8;

  const factors = [
    { name: 'OKR Progress', value: data.score.okrProgress, weight: '40%' },
    { name: 'Check-in Compliance', value: data.score.checkInCompliance, weight: '30%' },
    { name: 'Engagement', value: data.score.engagement, weight: '20%' },
    { name: 'Timeliness', value: data.score.timeliness, weight: '10%' },
  ];

  doc.setFont('helvetica', 'normal');
  factors.forEach((factor) => {
    doc.setFontSize(FONTS.small);
    setColor(doc, COLORS.dark);
    doc.text(factor.name, breakdownX, yPos);
    doc.text(factor.weight, breakdownX + 45, yPos);

    // Progress bar (width reduced to fit)
    drawProgressBar(doc, factor.value, breakdownX + 55, yPos - 4, 40);

    // Score value
    setColor(doc, getScoreColorRGB(factor.value));
    doc.text(`${Math.round(factor.value)}%`, breakdownX + 100, yPos);

    yPos += 10;
  });

  // Ensure we're past the gauge before continuing
  yPos = Math.max(yPos, breakdownYStart + gaugeSize + 15) + 10;

  // Summary
  if (data.summary) {
    yPos = checkPageBreak(doc, yPos, 40);
    yPos = drawSubsectionHeader(doc, 'Performance Summary', yPos);

    doc.setFontSize(FONTS.body);
    setColor(doc, COLORS.secondary);
    doc.setFont('helvetica', 'normal');
    const summaryLines = doc.splitTextToSize(data.summary, pageWidth - 40);
    doc.text(summaryLines, 20, yPos);
    yPos += summaryLines.length * 5 + 10;
  }

  // OKRs Table
  if (data.okrs && data.okrs.length > 0) {
    yPos = checkPageBreak(doc, yPos, 60);
    yPos = drawSubsectionHeader(doc, `OKRs (${data.okrs.length})`, yPos);

    const okrRows = data.okrs.slice(0, 15).map((okr) => [
      okr.title.substring(0, 40),
      okr.type,
      okr.status,
      okr.progress,
      `${okr.keyResultsCompleted}/${okr.keyResultsCount}`,
    ]);

    yPos = drawTable(
      doc,
      ['Objective', 'Type', 'Status', 'Progress', 'KRs'],
      okrRows,
      20,
      yPos,
      [70, 30, 30, 25, 20],
      { scoreColumn: 3 }
    );
  }

  // Improvement Suggestions
  if (data.improvements && data.improvements.length > 0) {
    yPos = checkPageBreak(doc, yPos, 50);
    yPos = drawSubsectionHeader(doc, 'Improvement Suggestions', yPos);

    const improvementRows = data.improvements.map((imp) => [
      imp.area,
      imp.recommendation.substring(0, 50),
      imp.priority.toUpperCase(),
      `+${imp.potentialGain}`,
    ]);

    yPos = drawTable(
      doc,
      ['Area', 'Recommendation', 'Priority', 'Potential'],
      improvementRows,
      20,
      yPos,
      [35, 80, 25, 25]
    );
  }

  return yPos;
}

/**
 * Render Team Performance Section
 */
function renderTeamSection(
  doc: jsPDF,
  data: TeamPerformanceSummary,
  yPos: number
): number {
  const pageWidth = doc.internal.pageSize.getWidth();

  doc.addPage();
  yPos = 20;

  // Section header
  yPos = drawSectionHeader(doc, 'Team Performance', yPos);
  yPos += 5;

  // Manager info
  doc.setFontSize(FONTS.body);
  setColor(doc, COLORS.secondary);
  doc.text(`Manager: ${data.managerName}`, 20, yPos);
  doc.text(`Team Size: ${data.teamSize} members`, pageWidth / 2, yPos);
  yPos += 12;

  // Team average score gauge
  const gaugeX = 45;
  const gaugeSize = 40;
  const gaugeYStart = yPos;
  drawScoreGauge(doc, data.averageScore, gaugeX, yPos + 20, gaugeSize);

  // Score distribution on the right (adjusted to fit page)
  const distX = 80;
  doc.setFontSize(FONTS.heading3);
  setColor(doc, COLORS.dark);
  doc.setFont('helvetica', 'bold');
  doc.text('Score Distribution', distX, yPos);
  yPos += 8;

  // Distribution chart width calculated to fit within page margins (pageWidth - distX - 20 margin)
  yPos = drawDistributionChart(doc, data.scoreDistribution, distX, yPos, pageWidth - distX - 25);

  // Ensure we're past the gauge before continuing
  yPos = Math.max(yPos, gaugeYStart + gaugeSize + 15) + 10;

  // Top Performers
  if (data.topPerformers && data.topPerformers.length > 0) {
    yPos = checkPageBreak(doc, yPos, 50);
    yPos = drawSubsectionHeader(doc, 'Top Performers', yPos);

    const topRows = data.topPerformers.slice(0, 5).map((p) => [
      p.userName,
      p.score,
      p.okrCount,
      getTrendText(p.trend),
    ]);

    yPos = drawTable(
      doc,
      ['Name', 'Score', 'OKRs', 'Trend'],
      topRows,
      20,
      yPos,
      [70, 30, 30, 25],
      { scoreColumn: 1, headerBg: COLORS.excellent }
    );
  }

  // Needs Attention
  if (data.needsAttention && data.needsAttention.length > 0) {
    yPos = checkPageBreak(doc, yPos, 50);
    yPos = drawSubsectionHeader(doc, 'Needs Attention', yPos);

    const attentionRows = data.needsAttention.slice(0, 5).map((p) => [
      p.userName,
      p.score,
      p.okrCount,
      p.lastCheckIn ? formatDate(p.lastCheckIn) : 'N/A',
    ]);

    yPos = drawTable(
      doc,
      ['Name', 'Score', 'OKRs', 'Last Check-in'],
      attentionRows,
      20,
      yPos,
      [70, 30, 30, 45],
      { scoreColumn: 1, headerBg: COLORS.warning }
    );
  }

  // All Direct Reports
  if (data.directReports && data.directReports.length > 0) {
    yPos = checkPageBreak(doc, yPos, 60);
    yPos = drawSubsectionHeader(doc, `All Team Members (${data.directReports.length})`, yPos);

    const reportRows = data.directReports.map((r) => [
      r.userName,
      r.userEmail,
      r.score,
      r.okrCount,
      getTrendText(r.trend),
    ]);

    yPos = drawTable(
      doc,
      ['Name', 'Email', 'Score', 'OKRs', 'Trend'],
      reportRows,
      20,
      yPos,
      [50, 55, 25, 25, 20],
      { scoreColumn: 2 }
    );
  }

  return yPos;
}

/**
 * Render Organization Performance Section
 */
function renderOrganizationSection(
  doc: jsPDF,
  data: OrganizationPerformanceSummary,
  yPos: number
): number {
  const pageWidth = doc.internal.pageSize.getWidth();

  doc.addPage();
  yPos = 20;

  // Section header
  yPos = drawSectionHeader(doc, 'Organization Performance', yPos);
  yPos += 5;

  // Organization summary cards
  const cardWidth = (pageWidth - 60) / 4;
  const cardHeight = 30;
  const cardY = yPos;

  const summaryCards = [
    { label: 'Total Employees', value: data.totalEmployees.toString(), color: COLORS.primary },
    { label: 'Active OKRs', value: data.activeOKRs.toString(), color: COLORS.secondary },
    { label: 'Departments', value: (data.departmentBreakdown?.length || 0).toString(), color: COLORS.secondary },
    { label: 'Org Average', value: Math.round(data.averageScore).toString(), color: getScoreColorRGB(data.averageScore) },
  ];

  summaryCards.forEach((card, i) => {
    const cardX = 20 + i * (cardWidth + 10);

    // Card background
    setFillColor(doc, COLORS.light);
    doc.roundedRect(cardX, cardY, cardWidth, cardHeight, 3, 3, 'F');

    // Label
    doc.setFontSize(FONTS.small);
    setColor(doc, COLORS.muted);
    doc.text(card.label, cardX + 5, cardY + 10);

    // Value
    doc.setFontSize(FONTS.heading1);
    setColor(doc, card.color);
    doc.setFont('helvetica', 'bold');
    doc.text(card.value, cardX + 5, cardY + 24);
    doc.setFont('helvetica', 'normal');
  });

  yPos = cardY + cardHeight + 15;

  // Score Distribution
  yPos = checkPageBreak(doc, yPos, 80);
  yPos = drawSubsectionHeader(doc, 'Organization Score Distribution', yPos);
  yPos = drawDistributionChart(doc, data.scoreDistribution, 20, yPos, pageWidth - 60);
  yPos += 10;

  // Department Breakdown
  if (data.departmentBreakdown && data.departmentBreakdown.length > 0) {
    yPos = checkPageBreak(doc, yPos, 60);
    yPos = drawSubsectionHeader(doc, `Department Performance (${data.departmentBreakdown.length})`, yPos);

    const deptRows = data.departmentBreakdown
      .sort((a, b) => b.averageScore - a.averageScore)
      .map((dept) => [
        dept.departmentName,
        dept.memberCount,
        dept.averageScore,
        getTrendText(dept.trend),
      ]);

    yPos = drawTable(
      doc,
      ['Department', 'Members', 'Avg Score', 'Trend'],
      deptRows,
      20,
      yPos,
      [70, 35, 35, 25],
      { scoreColumn: 2 }
    );
  }

  // Manager Breakdown
  if (data.managerBreakdown && data.managerBreakdown.length > 0) {
    yPos = checkPageBreak(doc, yPos, 60);
    yPos = drawSubsectionHeader(doc, `Manager Performance (${data.managerBreakdown.length})`, yPos);

    const managerRows = data.managerBreakdown
      .sort((a, b) => b.teamAverageScore - a.teamAverageScore)
      .map((mgr) => [
        mgr.managerName,
        mgr.teamSize,
        mgr.teamAverageScore,
        getTrendText(mgr.trend),
      ]);

    yPos = drawTable(
      doc,
      ['Manager', 'Team Size', 'Team Avg', 'Trend'],
      managerRows,
      20,
      yPos,
      [70, 35, 35, 25],
      { scoreColumn: 2 }
    );
  }

  return yPos;
}

/**
 * Render Table of Contents
 */
function renderTableOfContents(
  doc: jsPDF,
  sections: { title: string; page: number }[],
  yPos: number
): number {
  const pageWidth = doc.internal.pageSize.getWidth();

  yPos = drawSubsectionHeader(doc, 'Table of Contents', yPos);
  yPos += 5;

  sections.forEach((section, index) => {
    doc.setFontSize(FONTS.body);
    setColor(doc, COLORS.dark);
    doc.text(`${index + 1}. ${section.title}`, 25, yPos);

    // Dotted line
    const textWidth = doc.getTextWidth(`${index + 1}. ${section.title}`);
    const dotsStart = 25 + textWidth + 5;
    const dotsEnd = pageWidth - 40;

    setColor(doc, COLORS.muted);
    for (let x = dotsStart; x < dotsEnd; x += 3) {
      doc.text('.', x, yPos);
    }

    // Page number
    doc.text(section.page.toString(), pageWidth - 25, yPos, { align: 'right' });

    yPos += 8;
  });

  return yPos + 10;
}

// ============================================
// MAIN EXPORT FUNCTIONS
// ============================================

/**
 * Generate comprehensive PDF based on user role
 * - Employee: My Performance only
 * - Manager: My Performance + Team Performance
 * - Admin: My Performance + Team Performance + Organization
 */
export async function generateComprehensivePDF(
  data: ComprehensiveExportData,
  periodType: PeriodType,
  options: PDFExportOptions = {}
): Promise<Blob> {
  const doc = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'a4',
  });

  const periodLabel = data.individual?.period?.label || PERIOD_LABELS[periodType];
  const userName = data.individual?.userName || 'User';
  // Get tenant name from organization data or options
  const tenantName = data.organization?.tenantName || options.tenantName;

  // Determine report type based on available data
  let reportType: 'Individual' | 'Team' | 'Organization' = 'Individual';
  if (data.organization) {
    reportType = 'Organization';
  } else if (data.team) {
    reportType = 'Team';
  }

  // Header on first page
  let yPos = drawHeader(
    doc,
    options.title || `${reportType} Performance Report`,
    options.subtitle || userName,
    periodLabel,
    tenantName,
    describePerformanceScope(reportType, userName)
  );

  // Build sections list
  const sections: { title: string; page: number; render: () => number }[] = [];

  if (data.individual) {
    sections.push({
      title: 'My Performance',
      page: 1,
      render: () => renderIndividualSection(doc, data.individual!, yPos, true),
    });
  }

  if (data.team) {
    sections.push({
      title: 'Team Performance',
      page: sections.length + 1,
      render: () => renderTeamSection(doc, data.team!, 20),
    });
  }

  if (data.organization) {
    sections.push({
      title: 'Organization Performance',
      page: sections.length + 1,
      render: () => renderOrganizationSection(doc, data.organization!, 20),
    });
  }

  // Table of contents for multi-section reports
  if (sections.length > 1 && options.includeTableOfContents !== false) {
    yPos = renderTableOfContents(doc, sections, yPos);
    yPos += 10;
  }

  // Render each section
  sections.forEach((section, index) => {
    if (index === 0 && data.individual) {
      yPos = renderIndividualSection(doc, data.individual, yPos, true);
    } else if (section.title === 'Team Performance' && data.team) {
      renderTeamSection(doc, data.team, 20);
    } else if (section.title === 'Organization Performance' && data.organization) {
      renderOrganizationSection(doc, data.organization, 20);
    }
  });

  // Add footers to all pages
  const totalPages = doc.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    drawFooter(doc, i, totalPages);
  }

  return doc.output('blob');
}

/**
 * Generate individual performance PDF (legacy support)
 */
export async function generateIndividualPDF(
  data: PerformanceData,
  _reportElement?: HTMLElement | null,
  options: PDFExportOptions = {}
): Promise<Blob> {
  return generateComprehensivePDF({ individual: data }, data.period?.type || 'quarterly', options);
}

/**
 * Generate team performance PDF (legacy support)
 */
export async function generateTeamPDF(
  data: TeamPerformanceSummary,
  options: PDFExportOptions = {}
): Promise<Blob> {
  const doc = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'a4',
  });

  const periodLabel = data.period?.label || PERIOD_LABELS[data.period?.type || 'quarterly'];

  let yPos = drawHeader(
    doc,
    options.title || 'Team Performance Report',
    data.managerName,
    periodLabel,
    options.tenantName,
    describePerformanceScope('Team', data.managerName)
  );

  renderTeamSection(doc, data, yPos);

  const totalPages = doc.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    drawFooter(doc, i, totalPages);
  }

  return doc.output('blob');
}

/**
 * Generate organization performance PDF (legacy support)
 */
export async function generateOrgPDF(
  data: OrganizationPerformanceSummary,
  options: PDFExportOptions = {}
): Promise<Blob> {
  const doc = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'a4',
  });

  const periodLabel = data.period?.label || PERIOD_LABELS[data.period?.type || 'quarterly'];

  let yPos = drawHeader(
    doc,
    options.title || 'Organization Performance Report',
    data.tenantName,
    periodLabel,
    data.tenantName || options.tenantName,
    describePerformanceScope('Organization')
  );

  renderOrganizationSection(doc, data, yPos);

  const totalPages = doc.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    drawFooter(doc, i, totalPages);
  }

  return doc.output('blob');
}

/**
 * Generate PDF filename based on export type and period
 */
export function generatePDFFilename(
  type: 'individual' | 'team' | 'department' | 'organization' | 'comprehensive',
  periodType: PeriodType,
  userName?: string
): string {
  const date = new Date().toISOString().split('T')[0];
  const period = PERIOD_LABELS[periodType].toLowerCase().replace('-', '_');

  switch (type) {
    case 'individual':
      const name = userName?.toLowerCase().replace(/\s+/g, '_') || 'user';
      return `performance_${name}_${period}_${date}.pdf`;
    case 'team':
      return `team_performance_${period}_${date}.pdf`;
    case 'department':
      return `department_performance_${period}_${date}.pdf`;
    case 'organization':
      return `org_performance_${period}_${date}.pdf`;
    case 'comprehensive':
      return `performance_report_${period}_${date}.pdf`;
    default:
      return `performance_report_${date}.pdf`;
  }
}

/**
 * Download a blob as a file
 */
export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
